import React from 'react';
import { motion } from 'framer-motion';
import { MapPin } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';

const drcMapImage = "https://static.prod-images.emergentagent.com/jobs/0d77d791-1090-4f5a-a5b4-c78945c5298b/images/2c279865e97709c6cdfb130575cbfb62039996e7e644d2ddb815a67838b3144a.png";

const PulsingDot = ({ top, left, delay = 0 }) => (
  <motion.div
    initial={{ scale: 0, opacity: 0 }}
    whileInView={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true }}
    className="absolute"
    style={{ top, left }}
  >
    <div className="relative">
      <motion.div
        animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
        transition={{ duration: 2, repeat: Infinity, delay }}
        className="absolute w-6 h-6 bg-[#FF6B00] rounded-full -translate-x-1/2 -translate-y-1/2"
      />
      <div className="w-3 h-3 bg-[#FF6B00] rounded-full -translate-x-1/2 -translate-y-1/2 relative z-10" />
    </div>
  </motion.div>
);

export const InterventionZone = () => {
  const { t } = useLanguage();

  const locations = [
    { top: '30%', left: '45%', delay: 0 },
    { top: '50%', left: '60%', delay: 0.3 },
    { top: '40%', left: '35%', delay: 0.6 },
    { top: '60%', left: '50%', delay: 0.9 },
    { top: '35%', left: '55%', delay: 1.2 },
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#0B1320] relative overflow-hidden" data-testid="zone-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-[#FF6B00] text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-4">
              {t.zone.title}
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="zone-title"
            >
              {t.zone.subtitle}
            </h2>
            
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-[#FF6B00] flex items-center justify-center">
                <MapPin size={32} className="text-white" weight="fill" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {t.zone.coverage}
                </p>
                <p className="text-sm text-white/50 uppercase tracking-wider">République Démocratique du Congo</p>
              </div>
            </div>

            <p className="text-base text-white/70 leading-relaxed max-w-lg" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {t.zone.description}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-10 pt-10 border-t border-white/10">
              <div>
                <p className="text-3xl font-bold text-[#FF6B00]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>26</p>
                <p className="text-xs text-white/50 uppercase tracking-wider mt-1">Provinces</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#FF6B00]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>100%</p>
                <p className="text-xs text-white/50 uppercase tracking-wider mt-1">Couverture</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#FF6B00]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>24/7</p>
                <p className="text-xs text-white/50 uppercase tracking-wider mt-1">Support</p>
              </div>
            </div>
          </motion.div>

          {/* Right - Map */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative aspect-square max-w-lg mx-auto">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-[#FF6B00]/10 blur-3xl rounded-full" />
              
              {/* Map Image */}
              <img
                src={drcMapImage}
                alt="DRC Map"
                className="relative z-10 w-full h-full object-contain opacity-80"
              />

              {/* Pulsing Dots */}
              {locations.map((loc, index) => (
                <PulsingDot key={index} top={loc.top} left={loc.left} delay={loc.delay} />
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 50px,
            rgba(255,255,255,0.03) 50px,
            rgba(255,255,255,0.03) 51px
          ),
          repeating-linear-gradient(
            90deg,
            transparent,
            transparent 50px,
            rgba(255,255,255,0.03) 50px,
            rgba(255,255,255,0.03) 51px
          )`
        }} />
      </div>
    </section>
  );
};
